import { saveAs } from 'file-saver';
import { Component, createRef } from 'react';
import { merge, Subject, Subscription } from 'rxjs';
import { debounceTime, filter as rxFilter, map, delay, startWith, pairwise } from 'rxjs/operators';
import Dropdown from '../../../components/ui/dropdown/dropdown';
import { TabBody, TabHeader, TabsComponent } from '../../../components/ui/tabs/tabs.component';
import { IOC_TYPES } from '../../../context/ioc.types';
import { container, lazyInject } from '../../../context/module';
import { BasicValidators, FormControl, FormGroup, Validators } from '../../../models/forms';
import { Logger } from '../../../models/logger';
import { GAService } from '../../../services/analytics.service';
import { EnvironmentService } from '../../../services/environment.service';
import { FileUploadService } from '../../../services/file-upload.service';
import { SpinnerService } from '../../../services/spinner.service';
import { UIContext } from '../../../services/ui-context';
import { FileSelectEvent } from '../../../types/file-upload.types';
import { GAContants } from '../../../types/ga.types';
import { Dimension2D, Rectangle } from '../../../types/geo.types';
import { RouteProps } from '../../../types/route-types';
import { BrowserUtils } from '../../../ui-utils/browser-utils';
import { HTMLUtils } from '../../../utils/html.utils';
import { cloneDeep, round, toInteger } from '../../../utils/lodash-min';
import { RxUtils } from '../../../utils/rx-utils';
import { UrlUtils } from '../../../utils/url.utils';
import { WindowUtils } from '../../../utils/window.utils';
import Tooltip from '../../ui/tooltip/tooltip.component';
import styles from './photo-editor.component.module.scss';
import ResizeImageDownloadButtonComponent from './resize-image-download/resize-image-download.component';
import { CCanvas } from './utils/ccanvas';
import { CImage } from './utils/cimage';
import { Crop as CropTool } from './utils/crop';
import { Filter as FilterTool } from './utils/filter';
import { Flip as FlipTool } from './utils/flip';
import { ImageContainer } from './utils/image-container';
import { Photo } from './utils/photo';
import { ResizableContainerBoundsChangeEvent } from './utils/resizable_square';
import { Resize as ResizeTool } from './utils/resize';
import { Rotate as RotateTool } from './utils/rotate';
import { WindowService } from '../../../services/window.service';
import FileUploadComponent from '../../converter/file-upload/file-upload.component';
import MultiLevelDropdown, { MultiLevelDropdownItem } from '../../ui/multi-level-dropdown/multi-level-dropdown.component';
import { PromiseUtils } from '../../../utils/promise.utils';
import { ToastService } from '../../../services/toast.service';
import { GeoUtils } from '../../../utils/geo.utils';



export const CSS_TRASITION_TIMEOUT_MILLISECONDS = 800;

enum Tools {
    CROP = 'crop',
    FLIP = 'flip',
    ROTATE = 'rotate',
    RESIZE = 'resize',
}

export interface PhotoEditorProps extends RouteProps {
    fileSelectEvent: FileSelectEvent;
    startNew: () => void;
}

export interface PhotoEditorState {
    fileSelectBtnClicked: boolean;
}

interface ImageResizeCropOptionItem {
    readableName: string;
    value: any
}

interface ImageResizeCropOptionGroup {
    readableName: string;
    unitType: string;
    hasOptions: boolean;
    options: ImageResizeCropOptionItem[];
}

export class PhotoEditorComponent extends Component<PhotoEditorProps, PhotoEditorState> {
    private readonly logger = Logger.getLogger('PhotoEditorComponent');

    private photo: Photo;
    private container: ImageContainer;

    private flip: FlipTool;
    private rotateTool: RotateTool;
    private resizeTool: ResizeTool;
    private cropTool: CropTool;
    private filterTool: FilterTool;

    private tool: CropTool | ResizeTool | FlipTool | RotateTool | FilterTool;

    private resizeSubject: Subject<any>;

    private enable: boolean;

    private dim: ResizableContainerBoundsChangeEvent;

    private resizeFormGroup: FormGroup;
    private cropFormGroup: FormGroup;

    private imageResizeSizes = [
        {
            readableName: "Percentage",
            unitType: 'PERCENTAGE',
            hasOptions: true,
            options: [
                { readableName: 'Original', value: null, selected: true },
                { readableName: '25%', value: 25 },
                { readableName: '50%', value: 50 },
                { readableName: '75%', value: 75 },
                { readableName: '100%', value: 100 },
                { readableName: '150%', value: 150 },
                { readableName: '200%', value: 200 },
            ],
        },
        {
            readableName: "Screen Size (Height)",
            unitType: 'PIXEL',
            hasOptions: true,
            options: [
                { readableName: '170p', value: 170 },
                { readableName: '200p', value: 200 },
                { readableName: '315p', value: 315 },
                { readableName: '320p', value: 320 },
                { readableName: '396p', value: 396 },
                { readableName: '400p', value: 400 },
                { readableName: '500p', value: 500 },
                { readableName: '566p', value: 566 },
                { readableName: '627p', value: 627 },
                { readableName: '628p', value: 628 },
                { readableName: '851p', value: 851 },
                { readableName: '900p', value: 900 },
                { readableName: '1080p', value: 1080 },
                { readableName: '1200p', value: 1200 },
                { readableName: '1350p', value: 1350 },
                { readableName: '1500p', value: 1500 },
                { readableName: '1584p', value: 1584 },
                { readableName: '1600p', value: 1600 },
                { readableName: '1920p', value: 1920 },
            ]
        }
    ];
    private imageResizeDropdownOptions: MultiLevelDropdownItem[] = [];

    // .map(i => new MultiLevelDropdownSection<ImageResizeCropOptionGroup, ImageResizeCropOptionItem>(
    //     new MultiLevelDropdownHeader(i),
    //     i.options.map(o => new MultiLevelDropdownOption(o))
    // ));

    private imageCropSizes = [
        {
            readableName: "",
            unitType: 'RATIO',
            hasOptions: true,
            options: [
                { readableName: 'Original', value: null, selected: true },
            ],
        },
        {
            readableName: "Aspect Ratios",
            unitType: 'RATIO',
            hasOptions: true,
            options: [
                { readableName: '1:1', value: { width: 1, height: 1 } },
                { readableName: '4:3', value: { width: 4, height: 3 } },
                { readableName: '3:2', value: { width: 3, height: 2 } },
                { readableName: '16:9', value: { width: 16, height: 9 } },
                { readableName: '16:10', value: { width: 16, height: 10 } },
                { readableName: '2:1', value: { width: 2, height: 1 } },
            ],
        },
        {
            readableName: "Facebook",
            unitType: 'PIXEL',
            hasOptions: true,
            options: [
                { readableName: "170 x 170 pixels (Profile Photo)", value: { width: 170, height: 170 } },
                { readableName: "1200 x 628 pixels (Landscape)", value: { width: 1200, height: 628 } },
                { readableName: "628 x 1200 pixels (Portrait)", value: { width: 628, height: 1200 } },
                { readableName: "1200 x 1200 pixels (Square)", value: { width: 1200, height: 1200 } },
                { readableName: "1080 x 1920 pixels (Stories)", value: { width: 1080, height: 1920 } },
                { readableName: "851 x 315 pixels (Cover Photo)", value: { width: 851, height: 315 } },
            ]
        },
        {
            readableName: "Instagram",
            unitType: 'PIXEL',
            hasOptions: true,
            options: [
                { readableName: "320 x 320 pixels (Profile Photo)", value: { width: 320, height: 320 } },
                { readableName: "1080 x 566 pixels (Landscape)", value: { width: 1080, height: 566 } },
                { readableName: "1080 x 1350 pixels (Portrait)", value: { width: 1080, height: 1350 } },
                { readableName: "1080 x 1080 pixels (Square)", value: { width: 1080, height: 1080 } },
                { readableName: "1080 x 1920 pixels (Stories)", value: { width: 1080, height: 1920 } },
            ]
        },
        {
            readableName: "Twitter",
            unitType: 'PIXEL',
            hasOptions: true,
            options: [
                { readableName: "400 x 400 pixels (Profile Photo)", value: { width: 400, height: 400 } },
                { readableName: "1600 x 900 pixels (Landscape)", value: { width: 1600, height: 900 } },
                { readableName: "1080 x 1350 pixels (Portrait)", value: { width: 1080, height: 1350 } },
                { readableName: "1080 x 1080 pixels (Square)", value: { width: 1080, height: 1080 } },
                { readableName: "1500 x 500 pixels (Cover Photo)", value: { width: 1500, height: 500 } },
            ]
        },
        {
            readableName: "LinkedIn",
            unitType: 'PIXEL',
            hasOptions: true,
            options: [
                { readableName: "400 x 400 pixels (Profile Photo)", value: { width: 400, height: 400 } },
                { readableName: "1200 x 627 pixels (Landscape)", value: { width: 1200, height: 627 } },
                { readableName: "627 x 1200 pixels (Portrait)", value: { width: 627, height: 1200 } },
                { readableName: "1080 x 1080 pixels (Square)", value: { width: 1080, height: 1080 } },
                { readableName: "1584 x 396 pixels (Cover Photo)", value: { width: 1584, height: 396 } },
            ]
        },
        {
            readableName: "TikTok",
            unitType: 'PIXEL',
            hasOptions: true,
            options: [
                { readableName: "200 x 200 pixels (Profile Photo)", value: { width: 200, height: 200 } },
                { readableName: "1920 x 1080 pixels (Landscape)", value: { width: 1920, height: 1080 } },
                { readableName: "1080 x 1920 pixels (Portrait)", value: { width: 1080, height: 1920 } },
                { readableName: "1080 x 1080 pixels (Square)", value: { width: 1080, height: 1080 } },
                { readableName: "1080 x 1920 pixels (Stories)", value: { width: 1080, height: 1920 } },
            ]
        }
    ];
    private imageCropDropdownOptions: MultiLevelDropdownItem[] = [];
    // .map(i => new MultiLevelDropdownSection<ImageResizeCropOptionGroup, ImageResizeCropOptionItem>(
    //     new MultiLevelDropdownHeader(i),
    //     i.options.map(o => new MultiLevelDropdownOption(o))
    // ));

    private defaultRatio: any = { // TODO preact fix these used to be PSSelectItem
        id: 'original',
        item: {
            x: -1,
            y: -1,
        },
        text: 'Original',
        selected: true,
    };
    // ratios: any[] = [ // TODO preact fix these used to be PSSelectItem
    //     this.defaultRatio,
    //     {
    //         id: '1:1',
    //         item: {
    //             x: 1,
    //             y: 1,
    //         },
    //         text: '1:1',
    //         selected: false,
    //     },
    //     {
    //         id: '4:3',
    //         item: {
    //             x: 4,
    //             y: 3,
    //         },
    //         text: '4:3',
    //         selected: false,
    //     },
    //     {
    //         id: '3:2',
    //         item: {
    //             x: 3,
    //             y: 2,
    //         },
    //         text: '3:2',
    //         selected: false,
    //     },
    //     {
    //         id: '16:9',
    //         item: {
    //             x: 16,
    //             y: 9,
    //         },
    //         text: '16:9',
    //         selected: false,
    //     },
    //     {
    //         id: '2:1',
    //         item: {
    //             x: 2,
    //             y: 1,
    //         },
    //         text: '2:1',
    //         selected: false,
    //     },
    // ];
    private fileReady = false;
    private fullscreen = false;
    private loaded = false;

    private elementResizable = createRef<HTMLDivElement>();
    private containerEl = createRef<HTMLDivElement>();
    private fileUploadInput = createRef<HTMLDivElement>();

    private tabPane = null;
    private tabPaneResolver = null;
    private tabReadyPromise: Promise<void> = new Promise((res) => {
        this.tabPaneResolver = res;
    });

    @lazyInject(IOC_TYPES.SpinnerService) private spinner: SpinnerService;
    @lazyInject(IOC_TYPES.UIContext) private uiContext: UIContext;
    @lazyInject(IOC_TYPES.GAService) private gaService: GAService;
    @lazyInject(IOC_TYPES.EnvironmentService) private envService: EnvironmentService;
    @lazyInject(IOC_TYPES.FileUploadService) private fileUploadService: FileUploadService;
    @lazyInject(IOC_TYPES.WindowService) private windowService: WindowService;
    @lazyInject(IOC_TYPES.ToastService) private toastService: ToastService;
    private windowResizeFn = (e) => {
        this.resizeEventDispacher(e);
    }

    private cropFormGroupSubscription: Subscription;
    private resizeFormGroupSubscription: Subscription;

    readonly state: Readonly<PhotoEditorState> = { fileSelectBtnClicked: false, };
    constructor(props) {
        super(props);

        this.resizeSubject = new Subject();
        this.resizeSubject.pipe(debounceTime(500)).subscribe((event) => this.resizeEvent(event));

        window['that'] = this
    }

    private initializeResizeFormGroup() {
        RxUtils.safeCloseSubscriber(this.resizeFormGroupSubscription);
        this.resizeFormGroup = new FormGroup();
        // TODO Preact need to set min control
        // fb.group({
        //     width: [0, [Validators.required, Validators.min(100)]],
        //     height: [0, [Validators.required, Validators.min(100)]],
        //     keepAspectRatio: [true, [Validators.required]],
        // });
        this.resizeFormGroup.setControl('width', new FormControl(0));
        this.resizeFormGroup.setControl('height', new FormControl(0))
        this.resizeFormGroup.setControl('keepAspectRatio', new FormControl(true))
        this.resizeFormGroupSubscription = this.resizeFormGroup.valueChanges.subscribe({
            next: () => {
                this.forceUpdate();
            }
        });
    }

    private initializeCropFormGroup() {
        RxUtils.safeCloseSubscriber(this.cropFormGroupSubscription);
        // TODO Preact need transfer validators
        // this.cropFormGroup = fb.group({
        //     top: [0, [Validators.required]],
        //     left: [0, [Validators.required]],
        //     width: [0, [Validators.required, Validators.min(100)]],
        //     height: [0, [Validators.required, Validators.min(100)]],
        //     keepAspectRatio: [true, [Validators.required]],
        //     ratio: [this.defaultRatio, []],
        // });
        this.cropFormGroup = new FormGroup();
        this.cropFormGroup.setControl('top', new FormControl(0));
        this.cropFormGroup.setControl('left', new FormControl(0));
        this.cropFormGroup.setControl('width', new FormControl(0));
        this.cropFormGroup.setControl('height', new FormControl(0));
        this.cropFormGroup.setControl('keepAspectRatio', new FormControl(true));
        this.cropFormGroup.setControl('ratio', new FormControl(this.defaultRatio));
        this.cropFormGroupSubscription = this.cropFormGroup.valueChanges.subscribe({
            next: () => {
                this.forceUpdate();
            }
        });
    }

    componentDidMount() {
        const componentMountedTime = Date.now();
        this.windowService.onFileSelectBtnClicked.pipe(
            rxFilter(evt => evt.dispatchedAt.getTime() > componentMountedTime)
        ).subscribe({
            next: (data) => {
                if (!this.state.fileSelectBtnClicked) {
                    this.setState({
                        fileSelectBtnClicked: true,
                    });
                }
            }
        });
        if (!BrowserUtils.hasDocument()) {
            console.error("Requested execution aborted since document was not avalilable");
            return;
        }
        window.addEventListener('resize', this.windowResizeFn, { passive: true });

        if (this.props.fileSelectEvent) {
            this.fileSelected(this.props.fileSelectEvent)
        }
    }

    componentWillUnmount() {
        if (!BrowserUtils.hasDocument()) {
            console.error("Requested execution aborted since document was not avalilable");
            return;
        }
        window.removeEventListener('resize', this.windowResizeFn);
        this.loaded = false;
    }

    async fileSelected(ev: FileSelectEvent) {
        this.spinner.runWithLoader(async () => {
            if (!this.state.fileSelectBtnClicked) {
                this.fileUploadService.fileSelected();
            }
            this.initializeResizeFormGroup();
            this.initializeCropFormGroup();
            try {
                await this.unregisterTool();
                const file = ev.files[0] as File;
                this.photo = new Photo({ x: styles.x, y: styles.y });
                this.container = new ImageContainer(
                    this.photo,
                    this.containerEl.current,
                    (fn) => this.showWithSpinner(fn),
                    container,
                );
                await this.photo.load(file, this.container);
                this.fileReady = true;
                // TODO This is a hack to prevent crop square from overflowing at start time
                await BrowserUtils.dispatchResizeEvent(0);
                this.showEditor();
                if (window) {
                    if (window.location.href.indexOf('crop') > -1) {
                        await this.initCropTools();
                    } else if (window.location.href.indexOf('flip') > -1) {
                        await this.initFlipTools();
                    } else if (window.location.href.indexOf('rotate') > -1) {
                        await this.initRotateTools();
                    } else {
                        await this.initResizeTools();
                    }
                }
            } catch (e) {
                this.uiContext.errorToast('Error', e.message, false, false);
                this.logger.error({
                    message: 'Error selecting file for photo editing.',
                    error: e,
                });
            }
            WindowUtils.dispatchResizeEvent(1000);
            await PromiseUtils.delay(1000);
            this.loaded = true;
        }, 'Loading Image. Please Wait....');

    }

    private showEditor() {
        try {
            document.querySelector('.canvasAndImageHolderParent').classList.remove('hidden');
            document.querySelector('.nav-container').classList.remove('hidden');
        } catch (e) {
            this.logger.error(e);
        }
    }

    private async showWithSpinner(fn: () => void, message = null) {
        const instance = await this.spinner.show(message);
        setTimeout(async () => {
            try {
                await fn();
            } catch (e) {
                this.logger.error(e);
            }
            setTimeout(() => this.spinner.hide(instance), 0);
        }, 100);
    }

    private async initFlipTools() {
        if (this.showErrorIfNoFile()) {
            this.showWithSpinner(async () => {
                try {
                    if (!(this.tool instanceof FlipTool)) {
                        await this.unregisterTool();
                        this.flip = new FlipTool(this.photo, this.container, styles);
                        this.forceUpdate();
                        this.tool = this.flip;
                        await this.flip.load();
                        this.showTab(Tools.FLIP);
                    }
                } catch (e) {
                    this.logger.error(e);
                    this.uiContext.showErrorDialog(
                        {
                            title: 'Error', text: `Could not perform last operation successfuly.
                If error persist, please refresh the browser.` },
                    );
                }
            });
        }
    }

    private async initRotateTools() {
        if (this.showErrorIfNoFile()) {
            this.showWithSpinner(async () => {
                try {
                    if (!(this.tool instanceof RotateTool)) {
                        await this.unregisterTool();
                        this.rotateTool = new RotateTool(this.photo, this.container, styles);
                        this.forceUpdate();
                        this.tool = this.rotateTool;
                        await this.rotateTool.load();
                        this.showTab(Tools.ROTATE);
                    }
                } catch (e) {
                    this.logger.error(e);
                    this.uiContext.showErrorDialog(
                        {
                            title: 'Error', text: `Could not perform last operation successfuly.
                If error persist, please refresh the browser.` },
                    );
                }
            });
        }
    }

    private async initResizeTools() {
        console.time('initResizeTools');
        if (this.showErrorIfNoFile()) {
            this.imageResizeDropdownOptions = this.imageResizeSizes.map(i => {
                return {
                    isHeader: true,
                    hasOptions: true,
                    readableName: i.readableName,
                    value: i.options.map(v => {
                        return {
                            unitType: i.unitType,
                            selected: !!v.selected,
                            ...v
                        }
                    })
                }
            });

            this.initializeResizeFormGroup();

            this.showWithSpinner(async () => {
                try {
                    if (!(this.tool instanceof ResizeTool)) {
                        await this.unregisterTool();
                        this.resizeTool = new ResizeTool(this.photo, this.container);
                        this.forceUpdate();
                        this.setUpResizeToolValueChangeListener();
                        this.tool = this.resizeTool;
                        await this.resizeTool.load({
                            resizeFormGroup: this.resizeFormGroup,
                        });
                        this.showTab(Tools.RESIZE);
                        this.forceUpdate();

                        this.setUpResizeFormGroupListener();

                        BrowserUtils.dispatchResizeEvent(400).then(() => this.forceUpdate());
                        BrowserUtils.dispatchResizeEvent(1000).then(() => this.forceUpdate());
                    }
                } catch (e) {
                    this.logger.error({
                        error: e,
                        message: 'Error initializing resize tool (outer).',
                    });
                    this.uiContext.showErrorDialog(
                        {
                            title: 'Error', text: `Could not perform last operation successfuly.
                If error persist, please refresh the browser.` },
                    );
                }
            });
        }
        console.timeEnd('initResizeTools');
    }

    private setUpResizeToolValueChangeListener() {
        this.resizeTool.valueChanges.subscribe(
            (dim) => {
                this.dim = dim;
                const width = toInteger(Math.ceil((dim.width_percentage / 100) * this.photo.getEphemeralCanvas().width));
                const height = toInteger(Math.ceil((dim.height_percentage / 100) * this.photo.getEphemeralCanvas().height));
                this.resizeFormGroup.setValue({
                    width: width,
                    height: height,
                    keepAspectRatio: this.resizeTool.mrs.isAspectLocked,
                }, {
                    noEmit: true,
                },
                );
            },
        );
    }

    private setUpResizeFormGroupListener() {
        this.resizeFormGroup.valueChanges.subscribe({
            next: (v) => {
                const controlName = v.height ? 'height' : 'width';
                this.updateMRS(controlName, {
                    ...this.resizeFormGroup.value
                });
            }
        });
    }

    private setResizePreset(e: { unitType: any, value: number }) {
        const originalAspectRatio = this.resizeFormGroup.get('keepAspectRatio').value
        if (e.unitType == 'PERCENTAGE') {
            var scaleValue = e.value;
            const width = this.photo.getPersistedCanvas().width * scaleValue / 100;
            const height = this.photo.getPersistedCanvas().height * scaleValue / 100;
            this.resizeFormGroup.patchValue({
                keepAspectRatio: !!originalAspectRatio,
                width: width,
                height: height,
            });
        } else {
            const width = this.photo.getPersistedCanvas().height * e.value / this.photo.getPersistedCanvas().width;
            this.resizeFormGroup.patchValue({
                width: width,
                height: e.value,
            });
        }

        this.resizeFormGroup.setValue({ keepAspectRatio: !!originalAspectRatio });
    }

    private updateMRS(controllerName: string, value: { width: number, height: number, keepAspectRatio: boolean }) {
        this.resizeTool.mrs.isAspectLocked = value.keepAspectRatio;

        const originalDim = this.resizeTool.mrs.dim;

        let dim = Object.assign({}, this.dim, { isTrusted: false }, value);
        const dX = value.width - originalDim.width;
        const dY = value.height - originalDim.height;

        dim.height += dY;
        dim.width += dX;
        dim.top -= dY / 2;
        dim.left -= dX / 2;

        const sourceActualWidth = this.photo.getEphemeralCanvas().width;
        const targetActualWidth = value.width;

        const sourceActualHeight = this.photo.getEphemeralCanvas().height;
        const targetActualHeight = value.height;

        const containerRect = HTMLUtils.getElementBoundingRectangle(this.container.getElement());

        let dWidth = (containerRect.width / sourceActualWidth) * (targetActualWidth - sourceActualWidth);
        let mrsWidth = containerRect.width + dWidth;
        let mrsLeft = -1 * dWidth / 2;

        let dHeight = (containerRect.height / sourceActualHeight) * (targetActualHeight - sourceActualHeight);
        let mrsHeight = containerRect.height + dHeight;
        let mrsTop = -1 * dHeight / 2;

        if (value.keepAspectRatio) {
            if (controllerName === 'width') {
                dHeight = (dWidth * containerRect.height / containerRect.width);
                mrsHeight = containerRect.height + dHeight;
                mrsTop = -1 * (dHeight / 2);

                this.resizeFormGroup.patchValue({
                    height: round(mrsHeight * targetActualWidth / mrsWidth),
                }, { noEmit: true });

            } else if (controllerName === 'height') {
                dWidth = (dHeight * containerRect.width / containerRect.height);
                mrsWidth = containerRect.width + dWidth;
                mrsLeft = -1 * dWidth / 2;

                this.resizeFormGroup.patchValue({
                    width: round(mrsWidth * targetActualHeight / mrsHeight),
                }, { noEmit: true });
            }
        }

        const c = cloneDeep(originalDim);
        c.width = mrsWidth;
        c.left = mrsLeft;

        c.height = mrsHeight;
        c.top = mrsTop;

        this.resizeTool.mrs.setDim(c);
        this.resizeTool.mrs.updateDimPercentage();
        this.resizeTool.mrs.updateResizableContainer();
        // this.resize.mrs.emitFakeEvent();
    }

    private async initCropTools() {
        if (this.showErrorIfNoFile()) {
            this.imageCropDropdownOptions = this.imageCropSizes.map(i => {
                return {
                    isHeader: true,
                    hasOptions: true,
                    readableName: i.readableName,
                    value: i.options.map(v => {
                        return {
                            unitType: i.unitType,
                            selected: !!v.selected,
                            ...v
                        }
                    })
                }
            });

            this.initializeCropFormGroup();
            this.showWithSpinner(async () => {
                try {
                    if (!(this.tool instanceof CropTool)) {
                        await this.unregisterTool();
                        this.cropTool = new CropTool(this.photo, this.container);
                        this.forceUpdate();

                        this.tool = this.cropTool;
                        const fGroupValue = this.cropFormGroup.value;
                        await this.cropTool.load({
                            resizeOptionFormGroupValues: fGroupValue,
                            cropFormGroup: this.cropFormGroup,
                        });
                        this.showTab(Tools.CROP);
                        this.forceUpdate();
                        this.setUpCropToolValueChangeListener();
                        this.setUpCropFormGroupListener();

                        const rect = this.cropTool.fitMovableResizableSqureToImageWithRatio(this.cropFormGroup.get('ratio').value);
                        this.updateCropInputs(null, rect);
                        BrowserUtils.dispatchResizeEvent(400).then(() => this.forceUpdate());
                        BrowserUtils.dispatchResizeEvent(1000).then(() => this.forceUpdate());
                    }
                } catch (e) {
                    this.logger.error({
                        error: e,
                        message: 'Error initializing crop tool.',
                    });
                    this.uiContext.showErrorDialog(
                        {
                            title: 'Error', text: `Could not perform last operation successfuly.
                If error persist, please refresh the browser.` },
                    );
                }
            });
        }
    }

    private setUpCropToolValueChangeListener() {
        this.cropTool
            .valueChanges
            .pipe(startWith(null), pairwise())
            .pipe(debounceTime(20))
            .subscribe(
                ([prev, dim]) => {
                    const containerRect = HTMLUtils.getElementBoundingRectangle(this.container.getElement());
                    const top = this.photo.getEphemeralCanvas().height * dim.top / containerRect.height;
                    const left = this.photo.getEphemeralCanvas().width * dim.left / containerRect.width;
                    const width = this.photo.getEphemeralCanvas().width * dim.width / containerRect.width;
                    const height = this.photo.getEphemeralCanvas().height * dim.height / containerRect.height;

                    this.cropFormGroup.patchValue(
                        {
                            top: top,
                            left: left,
                            width: round(width),
                            height: round(height),
                        }
                    );
                },
            );
    }

    private setUpCropFormGroupListener() {
        this.cropFormGroup
            .valueChanges
            .pipe(startWith(null), pairwise())
            .subscribe({
                next: ([prev, current]) => {
                    const updatedKeys = Object.keys(current)

                    const sourceImageDim = { width: this.photo.originalImageWidth, height: this.photo.originalImageHeight };
                    const containerRect = HTMLUtils.getElementBoundingRectangle(this.container.getElement());

                    const keepAspectRatio = this.cropFormGroup.get('keepAspectRatio').value;
                    // left and top is included in mouse move events, so we need to skip resetting left and top
                    // for those crop requests
                    const hasOnlyHeightOrWidthChange = updatedKeys.length == 1 && (updatedKeys.includes('width') || updatedKeys.includes('height'))
                    const hasOnlyHeightAndWidthChange = updatedKeys.length == 2 && (updatedKeys.includes('width') && updatedKeys.includes('height'))
                    const resetTopLeft = updatedKeys.includes('left') || hasOnlyHeightOrWidthChange || hasOnlyHeightAndWidthChange ? false : true;

                    if (!updatedKeys.includes('height')) {
                        const currentHeight = this.cropFormGroup.get('height').value;
                        current.height = currentHeight;
                    }
                    if (!updatedKeys.includes('width')) {
                        const currentWidth = this.cropFormGroup.get('width').value;
                        current.width = currentWidth;
                    }

                    const dim = GeoUtils.matchRatio(
                        sourceImageDim,
                        containerRect,
                        { width: current.width, height: current.height, }
                    )
                    this.setCropRMS(dim, keepAspectRatio, resetTopLeft);
                }
            });
    }

    private setCropRMS(dim: Dimension2D, keepAspectRatio: boolean, resetTopLeft: boolean) {
        const rect = this.cropTool.setMRS(
            'width',
            {
                width: dim.width,
                height: dim.height,
                keepAspectRatio: keepAspectRatio,
                ratio: { id: `${dim.width}:${dim.height}` },
                resetTopLeft: resetTopLeft
            }
        );
        const rect2 = this.cropTool.setMRS(
            'height',
            {
                width: dim.width,
                height: dim.height,
                keepAspectRatio: keepAspectRatio,
                ratio: { id: `${dim.width}:${dim.height}` },
                resetTopLeft: resetTopLeft
            }
        );
        return [rect, rect2];
    }

    private updateCropInputs(e, targetRect?: Rectangle) {
        const rect = targetRect || this.cropTool.lastManuallyUpdateResults;
        if (rect) {
            const containerRect = HTMLUtils.getElementBoundingRectangle(this.container.getElement());

            const width = this.photo.getEphemeralCanvas().width * rect.width / containerRect.width;
            const height = this.photo.getEphemeralCanvas().height * rect.height / containerRect.height;

            this.cropFormGroup.patchValue(
                {
                    height: round(height),
                    width: round(width),
                }, {
                noEmit: false,
            },
            );
        } else {
            console.info('Dimensions were empty when input was unfocused');
        }
    }

    private cropInputChanged(controllerName: 'keepAspectRatio' | 'width' | 'height', value: number | boolean) {
        if (controllerName == 'keepAspectRatio') {
            this.cropFormGroup.patchValue(
                {
                    keepAspectRatio: !!value,
                },
            );
            return;
        }

        const sourceImageDim = { width: this.photo.originalImageWidth, height: this.photo.originalImageHeight };
        const resizableElementRect = HTMLUtils.getElementBoundingRectangle(this.container.getResizableElement());

        const currentValue = {
            width: this.cropFormGroup.value['width'],
            height: this.cropFormGroup.value['height'],
        }
        const length = value as number;

        var newValue: { width: number, height: number };
        if (!this.cropFormGroup.value['keepAspectRatio']) {
            const newWidth = controllerName == 'width' ? length : currentValue.width;
            const newHeight = controllerName == 'height' ? length : currentValue.height;
            newValue = {
                width: Math.floor(newWidth),
                height: Math.floor(newHeight),
            }

        } else {
            const newWidth = controllerName == 'width' ? length : length * resizableElementRect.width / resizableElementRect.height;
            const newHeight = controllerName == 'height' ? length : length * resizableElementRect.height / resizableElementRect.width;
            newValue = {
                width: Math.floor(newWidth),
                height: Math.floor(newHeight),
            }
        }

        const sourceRect = {
            top: 0, 
            left: 0, 
            ...sourceImageDim
        }
        const newRect = {
            top: this.cropFormGroup.value['top'], 
            left: this.cropFormGroup.value['left'], 
            ...newValue

        }
        if (GeoUtils.containsRect(sourceRect, newRect)) {
            this.cropFormGroup.patchValue(newValue);
        } else {
            this.uiContext.errorToast(`Oops`, `Value is larger`);
        }

    }

    private setCropValueForRatioSetting(e: any) {
        const canvasWidth = this.photo.getEphemeralCanvas().width;
        const canvasHeight = this.photo.getEphemeralCanvas().height;

        var width = e.value?.width ?? canvasWidth
        var height = e.value?.height ?? canvasHeight

        const area = GeoUtils.fitInSource({ width: canvasWidth, height: canvasHeight }, { width, height })

        // const area = this.computeFittingArea(canvasWidth, canvasHeight, width / height);
        width = area.width;
        height = area.height;

        const fn = () => {
            const currentAspectRatioSettings = this.cropFormGroup.get('keepAspectRatio').value
            // this.cropFormGroup.setValue({ keepAspectRatio: false, });
            this.cropFormGroup.setValue({
                ratio: {
                    id: `${width}:${height}`,
                    item: {
                        x: width,
                        y: height,
                    }
                },
                width: width,
                height: height,
                keepAspectRatio: currentAspectRatioSettings,
            });
        }
        fn()
    }

    private setCropValueForAbsoluteSetting(e: any) {
        const canvasWidth = this.photo.getEphemeralCanvas().width;
        const canvasHeight = this.photo.getEphemeralCanvas().height;

        var width = e.value?.width ?? canvasWidth
        var height = e.value?.height ?? canvasHeight

        const fn = () => {
            const currentAspectRatioSettings = this.cropFormGroup.get('keepAspectRatio').value
            // this.cropFormGroup.setValue({ keepAspectRatio: false, });
            this.cropFormGroup.setValue({
                ratio: {
                    id: `${width}:${height}`,
                    item: {
                        x: width,
                        y: height,
                    }
                },
                width: width,
                height: height,
                keepAspectRatio: currentAspectRatioSettings,
            });
        }
        fn()
    }

    private resizeAndSetCropValue(e: any): void {
        const canvasWidth = this.photo.getEphemeralCanvas().width;
        const canvasHeight = this.photo.getEphemeralCanvas().height;
        const imageWidth = this.cropTool.originalImageWidth;
        const imageHeight = this.cropTool.originalImageHeight;

        var width = e.value?.width ?? canvasWidth
        var height = e.value?.height ?? canvasHeight

        const requiredDims = GeoUtils.expandSourceToFitTarget(
            { width: imageWidth, height: imageHeight },
            { width: width, height: height },
        )

        // For the requested height, this is the width that we need to fit the mrs
        const requiredCanvasWidth = Math.ceil(requiredDims.width);
        // For the requested width, this is the height that we need to fit the mrs
        const requiredCanvasHeight = Math.ceil(requiredDims.height);


        const fn = () => {
            // const currentAspectRatioSettings = this.cropFormGroup.get('keepAspectRatio').value
            // this.cropFormGroup.setValue({ keepAspectRatio: false, });
            this.cropFormGroup.setValue({
                ratio: {
                    id: `${width}:${height}`,
                    item: {
                        x: width,
                        y: height,
                    }
                },
                width: width,
                height: height,
                keepAspectRatio: false,
            });
        }

        const newSize = {
            width: requiredCanvasWidth, height: requiredCanvasHeight,
        }

        const sub = this.cropTool.valueChanges.subscribe({
            next: (ev) => {
                setTimeout(() => {
                    fn();
                }, 1000)
                RxUtils.safeCloseSubscriber(sub);
            }
        });
        console.log(`New resize: `, newSize)
        this.cropTool.resizeImage({ ...newSize });

        this.toastService.warning(
            `Image Modified`,
            `We made your image bigger to fit the requested crop area`,
            {
                disableTimeOut: true,
                tapToDismiss: true
            }
        );
    }

    private setCropPreset(e: any): void {
        const canvasWidth = this.photo.getEphemeralCanvas().width;
        const canvasHeight = this.photo.getEphemeralCanvas().height;

        var width = e.value?.width ?? canvasWidth
        var height = e.value?.height ?? canvasHeight

        if (e.unitType == 'RATIO') {
            this.setCropValueForRatioSetting(e)
            return;
        }

        if (width > canvasWidth || height > canvasHeight) {
            this.uiContext.showConfirmationWarningDialog(
                `Warning`,
                `We need to resize the image to crop to selected dimension since Uploaded image is ${canvasWidth} by ${canvasHeight} and required crop area is ${width} by ${height}`,
                () => {
                    this.resizeAndSetCropValue(e)
                },
                true,
                'Cancel',
                'Ok! Resize',
            )
        } else {
            this.setCropValueForAbsoluteSetting({
                unitType: 'RATIO',
                value: {
                    width, height
                }
            });
        }
    }

    private async initFilterTools() {
        if (this.showErrorIfNoFile()) {
            this.showWithSpinner(async () => {
                try {
                    if (!(this.tool instanceof FilterTool)) {
                        await this.unregisterTool();
                        this.filterTool = new FilterTool(this.photo, this.container);
                        this.tool = this.filterTool;
                        await this.filterTool.load();
                    }
                } catch (e) {
                    this.logger.error({
                        error: e,
                        message: 'Error initializing filter tool.',
                    });
                    this.uiContext.showErrorDialog(
                        {
                            title: 'Error', text: `Could not perform last operation successfuly.
                If error persist, please refresh the browser.` },
                    );
                }

            });
        }
    }

    // TODO Preact dont have this, need to fix it
    // @HostListener('filter', ['$event.target'])
    async applyFilter(target: HTMLElement) {
        const filterName = target.dataset.filter;
        await this.filterTool.apply(filterName);
    }

    // TODO Preact dont have this, need to fix it
    // @HostListener('window:resize', ['$event'])
    async resizeEventDispacher(event) {
        this.resizeSubject.next(event);
    }

    async resizeEvent(event) {
        // this.container.notify(event.type);
        if (this.tool && this.tool.handleResizeEvent) {
            this.tool.handleResizeEvent();
        }
    }

    async flipHorizontally() {
        const instance = await this.spinner.show();
        await this.flip.flipHorizontally();
        this.spinner.hide(instance);
    }

    async flipVertically() {
        const instance = await this.spinner.show();
        await this.flip.flipVertically();
        this.spinner.hide(instance);
    }

    private async rotateCCW() {
        const instance = await this.spinner.show();
        await this.rotateTool.rotateCounterClockWise();
        this.spinner.hide(instance);
    }

    private async rotateCW() {
        await this.rotateTool.rotateClockWise();
    }

    private mouseDown(e) {
        this.container.updateParentDimension();
        if (this.tool instanceof ResizeTool) {
            if (e.target.classList.contains('resize_handle')) {
                this.enable = true;
            }
        }
        if (this.tool instanceof CropTool) {
            if (e.target.classList.contains('resize_handle')) {
                this.enable = true;
            }
            if (e.target.classList.contains('overlay')) {
                this.enable = true;
            }
        }
        this.tool.resizeMouseDown(e);

        WindowUtils.addMoveMoveHandlerToWindow((_e) => this.mouseMove(_e), true, (_e) => this.mouseUp(_e));
        this.forceUpdate();
    }

    private mouseMove(e) {
        if (this.enable) {
            this.tool.mouseMove && this.tool.mouseMove(e);
            if (this.tool instanceof CropTool) {
                this.tool.mouseMove(e);
            }
        }
        this.forceUpdate();
    }

    private mouseUp(e) {
        this.enable = false;
        this.forceUpdate();
    }

    async unregisterTool() {
        if (this.tool) {
            await this.tool.finalize();
            this.tool = undefined;
        }
    }

    private showErrorIfNoFile() {
        if (!this.fileReady) {
            this.uiContext.showErrorDialog({ title: 'Error', text: 'Please select a image file.' });
            return false;
        }
        return true;
    }

    private showTab(tabname: Tools) {
        this.tabReadyPromise.then(() => {
            const a = document.getElementsByClassName('tab-pane');
            for (let i = 0; i < a.length; i++) {
                a[i].classList.remove('display-block');
            }

            const element = document.querySelector(`.tab-pane[aria-labelledby=${tabname}-tab`);
            element.classList.add('display-block');
        });
    }

    private toggleFullscreen() {
        this.fullscreen = !this.fullscreen;
        if (this.fullscreen) {
            document.getElementsByTagName('body')[0].classList.add('overflow-hidden');
        } else {
            document.getElementsByTagName('body')[0].classList.remove('overflow-hidden');
        }
    }

    private saveResize() {
        this.resizeTool.apply();
    }

    private resetResize() {
        this.resizeTool.handleResizeEvent();
    }

    private saveCrop() {
        this.cropTool.apply();
    }

    private resetCrop() {
        this.cropTool.handleResizeEvent();
    }

    private async showDownload(fileType: string) {
        this.showWithSpinner(() => this._showDownload(fileType), 'We are preparing you file. Please wait...');
    }

    private async _showDownload(fileType: string) {
        if (this.tool instanceof ResizeTool) {
            this.saveResize();
        } else if (this.tool instanceof CropTool) {
            this.saveCrop();
        }
        const translatedFileype = fileType === 'jpeg' ? 'jpg' : fileType;
        const translatedFileypeForCanvas = fileType === 'jpg' ? 'jpeg' : fileType;
        let blob = null;
        if (translatedFileype.indexOf('bmp') > -1) {
            blob = CImage.canvasToBMPBlob(this.photo.getEphemeralCanvas());
        } else if (translatedFileype.indexOf('tiff') > -1) {
            blob = await CImage.canvasToTIFFBlob(this.photo.getEphemeralCanvas());
        } else if (translatedFileype.indexOf('webp') > -1) {
            blob = await CCanvas.canvasToWebPBlob(this.photo.getEphemeralCanvas(), 100);
        } else {
            blob = await CImage.canvasToBlob(this.photo.getEphemeralCanvas(), translatedFileypeForCanvas);
        }
        saveAs(blob, `${this.envService.env.id}.com.${translatedFileype}`);
        this.gaService.send(
            GAContants.HitType.EVENT,
            GAContants.EventCategory.BUTTON,
            GAContants.EventAction['FILE-DOWNLOAD'],
            GAContants.EventLabel.DOWNLOAD,
            '',
        );
    }

    private tabChanged(tabname: any) {
        switch (tabname) {
            case 'flip':
                this.initFlipTools();
                break;
            case 'rotate':
                this.initRotateTools();
                break;
            case 'resize':
                this.initResizeTools();
                break;
            case 'crop':
                this.initCropTools();
                break;
            default:
                throw new Error('Unknown feature')
        }
        this.showTab(tabname)
    }

    get activeTabName() {
        return UrlUtils.getUrlPathnameWithoutSlash(location).split('-')[0];
    }

    private setTabPaneRef(element) {
        this.tabPane = element;
        this.tabPaneResolver();
    }

    render() {
        return (
            <div style={{ visibility: this.loaded ? 'visible' : 'hidden' }}>
                {
                    false && <div className={`col-12 col-sm-12 col-md-12 col-lg-12`}>
                        <div className={`row nav-padding static-section ${styles['static-sectionpage-heading']}`}>
                            <div className={`col-12 col-sm-12 col-md-12 col-lg-12`}>
                                <FileUploadComponent {...this.props}
                                    fileSelected={$event => this.fileSelected($event)}
                                    fileSelectButtonClicked={() => console.log('Clicked')}
                                    acceptedFileTypes="image/*"
                                    uploadBtnText="Select Image" showGoogleDrive={false} showDropbox={false}>
                                </FileUploadComponent>
                            </div>
                        </div>
                    </div>
                }
                <div className={`${styles['tools-container']}`}>
                    <div>
                        {
                            this.fileReady && <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                                <TabsComponent activeTabName={this.activeTabName} onTabChange={(tabname) => this.tabChanged(tabname)}>
                                    <TabHeader headerFor="flip" type="header">
                                        Flip
                                    </TabHeader>
                                    <TabHeader headerFor="rotate" type="header">
                                        Rotate
                                    </TabHeader>
                                    <TabHeader headerFor="resize" type="header">
                                        Resize
                                    </TabHeader>
                                    <TabHeader headerFor="crop" type="header">
                                        Crop
                                    </TabHeader>
                                    <TabBody name="flip" type="body">
                                        <div className={`${styles['tab-pane']} ${styles['flip-tab-pane']} tab-pane display-none`}
                                            id="flip" role="tabpanel"
                                            aria-labelledby="flip-tab"
                                            ref={(element) => this.setTabPaneRef(element)}
                                        >
                                            <div className={styles['single-row-tab-content']}>
                                                <div className={styles['single-row-buttons']}>
                                                    <Tooltip text="Flip horizontal">
                                                        <button className={`btn btn-secondary btn-sm btn-svg-with-text`} onClick={e => this.flipHorizontally()}>
                                                            <img width={12} src="/icons/svg/flip-horizontal.svg" /> <span>Flip Horizontal</span>
                                                        </button>
                                                    </Tooltip>
                                                    <Tooltip text="Flip vertical">
                                                        <button className={`btn btn-secondary btn-sm btn-svg-with-text`} onClick={e => this.flipVertically()}>
                                                            <img width={12} src="/icons/svg/flip-vertical.svg" /> <span>Flip Vertical</span>
                                                        </button>
                                                    </Tooltip>
                                                </div>
                                                <ResizeImageDownloadButtonComponent
                                                    showDownload={($event => this.showDownload($event))}
                                                    btnSizeClass='btn-sm'>
                                                </ResizeImageDownloadButtonComponent>
                                            </div>
                                        </div>
                                    </TabBody>
                                    <TabBody name="rotate" type="body">
                                        <div className={`${styles['tab-pane']} tab-pane display-none`} id="rotate" role="tabpanel" aria-labelledby="rotate-tab">
                                            <div className={styles['single-row-tab-content']}>
                                                <div className={styles['single-row-buttons']}>
                                                    <Tooltip text="Rotate to left">
                                                        <button className={`btn btn-secondary btn-sm btn-svg-with-text`} onClick={e => this.rotateCCW()}>
                                                            <img width={12} src="/icons/svg/arrow-counterclockwise.svg" /> <span>Rotate Left</span>
                                                        </button>
                                                    </Tooltip>
                                                    <Tooltip text="Rotate to right">
                                                        <button className={`btn btn-secondary btn-sm btn-svg-with-text`} onClick={e => this.rotateCW()}>
                                                            <img width={12} src="/icons/svg/arrow-clockwise.svg" /> <span>Rotate Right</span>
                                                        </button>
                                                    </Tooltip>
                                                </div>
                                                <ResizeImageDownloadButtonComponent showDownload={($event => this.showDownload($event))} btnSizeClass='btn-sm'
                                                >
                                                </ResizeImageDownloadButtonComponent>
                                            </div>
                                        </div>
                                    </TabBody>
                                    <TabBody name="resize" type="body">
                                        <div className={`${styles['tab-pane']} tab-pane display-none`} id="resize" role="tabpanel" aria-labelledby="resize-tab">
                                            {
                                                this.resizeTool && <div>Select a file to see resize options.</div>
                                            }
                                            {
                                                this.resizeTool && (
                                                    <div className={styles['resize-tab-content']}>
                                                        <div className={styles['resize-options']}>
                                                            <div className={styles['single-row-tab-content']}>
                                                                <div className={styles['single-row-buttons']}>
                                                                    <div>
                                                                        <label className={`sr-only`} htmlFor="resizeToolWidth">W</label>
                                                                        <div className={`input-group input-group-sm`}>
                                                                            <div className={`input-group-prepend`}>
                                                                                <div className={`input-group-text`}>W</div>
                                                                            </div>
                                                                            <input type="number" className={`form-control`} id="resizeToolWidth" placeholder="Width"
                                                                                value={this.resizeFormGroup.controls['width'].value}
                                                                                onChange={(e: any) => this.resizeFormGroup.setValue({ width: e.target.valueAsNumber })} />
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <div className={`input-group content-centered`}>
                                                                            <Tooltip text="Lock aspect ratio">
                                                                                <button className={`btn btn-secondary btn-svg`}
                                                                                    onClick={e => this.resizeFormGroup.setValue({ 'keepAspectRatio': !this.resizeFormGroup.controls['keepAspectRatio'].value })}>
                                                                                    {
                                                                                        this.resizeFormGroup.controls['keepAspectRatio'].value && <img src="/icons/svg/lock.svg" />
                                                                                    }
                                                                                    {
                                                                                        !this.resizeFormGroup.controls['keepAspectRatio'].value && <img src="/icons/svg/unlock.svg" />
                                                                                    }
                                                                                </button>
                                                                            </Tooltip>
                                                                        </div>
                                                                    </div>

                                                                    <div>
                                                                        <label className={`sr-only`} htmlFor="resizeToolHeight">H</label>
                                                                        <div className={`input-group input-group-sm`}>
                                                                            <div className={`input-group-prepend`}>
                                                                                <div className={`input-group-text`}>H</div>
                                                                            </div>
                                                                            <input type="number" className={`form-control`} id="resizeToolHeight" placeholder="Height"
                                                                                value={this.resizeFormGroup.controls['height'].value}
                                                                                onChange={(e: any) => this.resizeFormGroup.setValue({ height: e.target.valueAsNumber })} />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className={styles['preset-sizes']}>
                                                                <MultiLevelDropdown
                                                                    // btnClass="btn btn-secondary"
                                                                    label="Presets"
                                                                    initialValue={this.imageResizeDropdownOptions[0].value[0]}
                                                                    values={this.imageResizeDropdownOptions}
                                                                    onSelect={e => this.setResizePreset(e)}
                                                                    valueFormatter={v => {
                                                                        return v?.readableName;
                                                                    }}
                                                                    hasTooltip={(v) => false}
                                                                    getTooltip={(v) => null}
                                                                    getOptions={(v) => { return v.options }}
                                                                ></MultiLevelDropdown>
                                                            </div>
                                                        </div>
                                                        <div className={styles['apply-and-save-btns']}>
                                                            <div className={styles['apply-btns']}>
                                                                <button className={`btn btn-primary btn-sm`}
                                                                    onClick={(e) => this.saveResize()}>Apply</button>
                                                                <button className={`btn btn-secondary btn-sm`}
                                                                    onClick={(e) => this.resetResize()}>Reset</button>
                                                            </div>
                                                            {/* className={`selectable`} */}
                                                            <ResizeImageDownloadButtonComponent showDownload={($event => this.showDownload($event))}
                                                                btnSizeClass='btn-sm'
                                                            >
                                                            </ResizeImageDownloadButtonComponent>
                                                        </div>
                                                    </div>
                                                )
                                            }
                                        </div>
                                    </TabBody>
                                    <TabBody name="crop" type="body">
                                        <div className={`${styles['tab-pane']} tab-pane display-none`} id="crop" role="tabpanel" aria-labelledby="crop-tab">
                                            {
                                                this.cropTool && (
                                                    <div className={styles['resize-tab-content']}>
                                                        <div className={styles['resize-options']}>
                                                            <div className={styles['single-row-tab-content']}>
                                                                <div className={styles['single-row-buttons']}>
                                                                    <div>
                                                                        <label className={`sr-only`} htmlFor="cropToolWidth">W</label>
                                                                        <div className={`input-group input-group-sm`}>
                                                                            <div className={`input-group-prepend`}>
                                                                                <div className={`input-group-text`}>W</div>
                                                                            </div>
                                                                            <input type="number" className={`form-control`}
                                                                                // onFocus={$event => this.updateCropInputs($event)}
                                                                                id="cropToolWidth" placeholder="Width"
                                                                                value={this.cropFormGroup.controls['width'].value}
                                                                                onChange={(e: any) => this.cropInputChanged('width', e.target.valueAsNumber)} />
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <div className={`input-group content-centered`}>
                                                                            <Tooltip text="Lock aspect ratio">
                                                                                <button className={`btn btn-secondary btn-svg`}
                                                                                    onClick={e => this.cropInputChanged('keepAspectRatio', !this.cropFormGroup.controls['keepAspectRatio'].value)}>
                                                                                    {
                                                                                        this.cropFormGroup.controls['keepAspectRatio'].value && <img src="/icons/svg/lock.svg" />
                                                                                    }
                                                                                    {
                                                                                        !this.cropFormGroup.controls['keepAspectRatio'].value && <img src="/icons/svg/unlock.svg" />
                                                                                    }
                                                                                </button>
                                                                            </Tooltip>
                                                                        </div>
                                                                    </div>

                                                                    <div>
                                                                        <label className={`sr-only`} htmlFor="cropToolHeight">H</label>
                                                                        <div className={`input-group input-group-sm`}>
                                                                            <div className={`input-group-prepend`}>
                                                                                <div className={`input-group-text`}>H</div>
                                                                            </div>
                                                                            <input type="number" className={`form-control`}
                                                                                // onFocus={$event => this.updateCropInputs($event)}
                                                                                id="cropToolHeight" placeholder="Height"
                                                                                value={this.cropFormGroup.controls['height'].value}
                                                                                onChange={(e: any) => this.cropInputChanged('height', e.target.valueAsNumber)} />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <MultiLevelDropdown
                                                                    // btnClass="btn btn-secondary"
                                                                    label="Presets"
                                                                    initialValue={this.imageCropDropdownOptions[0].value[0]}
                                                                    values={this.imageCropDropdownOptions}
                                                                    onSelect={e => this.setCropPreset(e)}
                                                                    valueFormatter={v => {
                                                                        return v?.readableName;
                                                                    }}
                                                                    hasTooltip={(v) => false}
                                                                    getTooltip={(v) => null}
                                                                    getOptions={(v) => { return v.options }}
                                                                ></MultiLevelDropdown>
                                                            </div>
                                                        </div>
                                                        <div className={styles['apply-and-save-btns']}>
                                                            <div className={styles['apply-btns']}>
                                                                <button className={`btn btn-primary btn-sm`}
                                                                    onClick={(e) => this.saveCrop()}>Apply</button>
                                                                <button className={`btn btn-secondary btn-sm`}
                                                                    onClick={(e) => this.resetCrop()}>Reset</button>
                                                            </div>
                                                            {/* className={`selectable`} */}
                                                            <ResizeImageDownloadButtonComponent showDownload={($event => this.showDownload($event))} btnSizeClass='btn-sm'
                                                            >
                                                            </ResizeImageDownloadButtonComponent>
                                                        </div>
                                                    </div>
                                                )
                                            }
                                        </div>

                                    </TabBody>
                                </TabsComponent>
                                <Tooltip text="Start new">
                                    <button className={`btn btn-primary btn-sm ${styles['start-new-btn']}`} onClick={e => this.props.startNew()}>
                                        + Start New
                                    </button>
                                </Tooltip>
                            </div>
                        }
                    </div>
                </div>
                <div className={`content-holder-parent mt-3 ${styles['content-holder-parent']}`}>
                    <div className={`${styles["col-lg-12"]} ${styles["canvasAndImageHolderParent"]} ${styles["hidden"]} canvasAndImageHolderParent`}>
                        <div className={`${styles["row"]}`}>
                            <div className={`${styles["col-lg-12"]} canvasAndImageHolder`}>
                                <div className={`${styles["content-holder"]}`}>
                                    <div id='container' className={styles.container} ref={this.containerEl}>
                                        <div id="top" className={`${styles["handle"]}`} style={{ backgroundColor: 'rgba(0, 0, 0, 0.64)', position: 'absolute' }}>
                                        </div>
                                        <div id="right" className={`${styles["handle"]}`}
                                            style={{ backgroundColor: 'rgba(0, 0, 0, 0.64)', position: 'absolute' }}>
                                        </div>
                                        <div id="left" className={`${styles["handle"]}`} style={{ backgroundColor: 'rgba(0, 0, 0, 0.64)', position: 'absolute' }}>
                                        </div>
                                        <div id="elementResizable" className={`${styles["elementResizable"]}`} ref={this.elementResizable}>
                                            <div className={`${styles["row"]} ${styles["grid"]}`}>
                                                <div className={`${styles["col-lg-12"]}`}>
                                                    <div className={`${styles["col-lg-4"]}`}></div>
                                                    <div className={`${styles["col-lg-4"]}`}></div>
                                                    <div className={`${styles["col-lg-4"]}`}></div>
                                                    <div className={`${styles["col-lg-4"]}`}></div>
                                                    <div className={`${styles["col-lg-4"]}`}></div>
                                                    <div className={`${styles["col-lg-4"]}`}></div>
                                                    <div className={`${styles["col-lg-4"]}`}></div>
                                                    <div className={`${styles["col-lg-4"]}`}></div>
                                                    <div className={`${styles["col-lg-4"]}`}></div>
                                                </div>
                                            </div>
                                            <div id="overlay" className={`move_handle ${styles["row"]} ${styles["overlay"]} ${styles["move_handle"]} overlay`}
                                                onMouseDown={$event => this.mouseDown($event)}
                                                onTouchStart={$event => this.mouseDown($event)}>
                                                <canvas id="overlayCanvas" className={`canvas ${styles.overlayCanvas}`}></canvas>
                                            </div>
                                            <div className={`resize_handle n w nwgrip ${styles["nwgrip"]}`}
                                                onMouseDown={$event => this.mouseDown($event)}
                                                onTouchStart={$event => this.mouseDown($event)} data-direction="nw" id="nwgrip"></div>
                                            <div className={`resize_handle n e negrip ${styles["negrip"]}`}
                                                onMouseDown={$event => this.mouseDown($event)}
                                                onTouchStart={$event => this.mouseDown($event)} data-direction="ne" id="negrip"></div>
                                            <div className={`resize_handle s w swgrip ${styles["swgrip"]}`}
                                                onMouseDown={$event => this.mouseDown($event)}
                                                onTouchStart={$event => this.mouseDown($event)} data-direction="sw" id="swgrip"></div>
                                            <div className={`resize_handle s e segrip ${styles["segrip"]}`}
                                                onMouseDown={$event => this.mouseDown($event)}
                                                onTouchStart={$event => this.mouseDown($event)} data-direction="se" id="segrip">
                                            </div>
                                        </div>
                                        <div id="bottom" className={`${styles["handle"]}`}
                                            style={{ backgroundColor: 'rgba(0, 0, 0, 0.64)', position: 'absolute' }}>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
